// Tindak Lanjut Pesanan — BUSINESS-50/51/52 (keputusan Owner 26 Sep 2026, VONIS-26SEP-PAKET-K3).
//
//   Menunggu DP   FA yang sudah disetujui customer tapi DP belum masuk: umur HARI KERJA, pengingat H+3/H+7 (CS
//                 mengingatkan customer), lewat 14 hari kerja CS mengajukan batal → Kepala Cabang region memutus.
//                 Tanda jadi hangus (Q11).
//   Tunggakan     customer yang menunggak pelunasan lewat H+7 (K116): tanda jadi, desain, dan FA tetap jalan, tapi
//                 FA-nya BELUM BOLEH MASUK PRODUKSI (K201 butir 6); CS/KC mengajukan izin, Owner memutus. Satu izin =
//                 satu FA.
//   Barang jadi   barang siap yang belum diambil, lunas atau belum (K201 butir 5): H+30/H+60 KC mengirim
//                 pemberitahuan tertulis lewat WA CS; lewat 90 hari masuk daftar keputusan Owner.
//
// ⚠ Layar ini hanya PINTU: peran, wilayah, pemohon ≠ penyetuju, dan batas hari ditegakkan
//   `backend/services/tindak_lanjut_pesanan.py`. Tombol mengikuti peran supaya orang tidak menekan yang pasti ditolak.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { apiErrorMessage } from "../lib/inventoryApi";
import { petaNamaCustomer } from "../lib/petaNamaCustomer";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import Hint from "../components/etams/Hint";

const TAB = [
  { kunci: "dp", judul: "Menunggu DP" },
  { kunci: "tunggakan", judul: "Customer menunggak" },
  { kunci: "barang", judul: "Barang jadi belum diambil" },
];

const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");

export default function TindakLanjutPesanan() {
  const { profile } = useAuth();
  const role = profile?.role?.code;
  const [params, setParams] = useSearchParams();
  const tab = TAB.some((t) => t.kunci === params.get("tab")) ? params.get("tab") : "dp";
  const [dialog, setDialog] = useState(null);   // { judul, wajib, kirim(alasan) }

  return (
    <div className="space-y-4" data-testid="tindak-lanjut-page">
      <h1 className="text-2xl font-bold">Tindak Lanjut Pesanan</h1>
      <div className="flex flex-wrap gap-2">
        {TAB.map((t) => (
          <Button key={t.kunci} size="sm" variant={tab === t.kunci ? "default" : "outline"}
                  onClick={() => setParams({ tab: t.kunci })} data-testid={`tindak-lanjut-tab-${t.kunci}`}>
            {t.judul}
          </Button>
        ))}
      </div>
      {tab === "dp" && <BagianDp role={role} setDialog={setDialog} />}
      {tab === "tunggakan" && <BagianTunggakan role={role} setDialog={setDialog} />}
      {tab === "barang" && <BagianBarang role={role} />}
      {dialog && <DialogAlasan {...dialog} onTutup={() => setDialog(null)} />}
    </div>
  );
}

function useMuat(url, params) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const muat = useCallback(async () => {
    setErr(null);
    try {
      const r = await api.get(url, params ? { params } : undefined);
      setData(r.data?.items || []);
    } catch (e) {
      setErr(apiErrorMessage(e, "Gagal memuat daftar."));
    }
  }, [url, JSON.stringify(params || null)]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { muat(); }, [muat]);
  return [data, err, muat];
}

function useNama(rows) {
  const [nama, setNama] = useState({});
  useEffect(() => {
    const ids = (rows || []).map((r) => r.customer_id).filter(Boolean);
    if (!ids.length) return;
    petaNamaCustomer(ids).then(setNama).catch((e) => toast.error(apiErrorMessage(e, "Nama customer gagal dimuat.")));
  }, [rows]);
  return nama;
}

function Kosong({ err, data, children }) {
  if (err) return <div className="p-4 text-sm text-danger-soft-foreground">{err}</div>;
  if (!data) return <div className="p-4 text-sm text-muted-foreground">Memuat…</div>;
  if (!data.length) return <div className="px-4 py-3 text-sm text-muted-foreground">Tidak ada.</div>;
  return children;
}

async function aksi(fn, pesanOk, muat) {
  try {
    await fn();
    toast.success(pesanOk);
    await muat();
    return true;
  } catch (e) {
    toast.error(apiErrorMessage(e, "Gagal menyimpan."));
    return false;
  }
}

// --- BUSINESS-50 --------------------------------------------------------------------------
function BagianDp({ role, setDialog }) {
  const [rows, err, muat] = useMuat("/tindak-lanjut/menunggu-dp");
  const [ajuan, errA, muatA] = useMuat("/tindak-lanjut/pengajuan-batal?status=DIAJUKAN");
  const nama = useNama(rows);
  const muatSemua = async () => { await muat(); await muatA(); };

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden" data-testid="tindak-lanjut-dp">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-semibold">
            FA menunggu DP {rows && <span className="text-muted-foreground font-normal">({rows.length})</span>}{" "}
            <Hint>Umur dihitung dalam hari kerja sejak customer menyetujui FA. H+3 dan H+7: CS mengingatkan customer.</Hint>
          </div>
          <div className="text-xs text-muted-foreground">
            Lewat 14 hari kerja CS boleh mengajukan batal — tanda jadi hangus.
          </div>
        </div>
        <Kosong err={err} data={rows}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr><th className="text-left px-4 py-2">FA</th><th className="text-left">Customer</th>
                  <th className="text-left">Hari kerja</th><th className="text-left">Keadaan</th><th className="text-right px-4">Aksi</th></tr>
              </thead>
              <tbody>
                {(rows || []).map((f) => (
                  <tr key={f.id} className="border-b border-border" data-testid={`tindak-lanjut-dp-${f.id}`}>
                    <td className="px-4 py-2 font-medium"><Link to={`/fa-orders/${f.id}`} className="underline">{f.fa_number}</Link></td>
                    <td>{nama[f.customer_id] || "—"}</td>
                    <td>{f.umur_hari_kerja ?? "—"}</td>
                    <td>
                      {f.pengajuan ? <span className="text-warning-soft-foreground">batal diajukan — menunggu Kepala Cabang</span>
                        : f.layak_diajukan_batal ? <span className="text-danger-soft-foreground">lewat 14 hari kerja</span>
                          : f.tahap === "dp_h7" ? "ingatkan lagi (H+7)"
                            : f.tahap === "dp_h3" ? "ingatkan customer (H+3)" : "menunggu"}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {role === "CS" && f.layak_diajukan_batal && !f.pengajuan && (
                        <Button size="sm" variant="outline" data-testid={`tindak-lanjut-ajukan-batal-${f.id}`}
                                onClick={() => setDialog({
                                  judul: `Ajukan batal ${f.fa_number}`, wajib: true,
                                  kirim: (alasan) => aksi(() => api.post(`/tindak-lanjut/menunggu-dp/${f.id}/ajukan-batal`, { alasan }),
                                    "Pengajuan batal terkirim ke Kepala Cabang", muatSemua),
                                })}>Ajukan batal</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Kosong>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="tindak-lanjut-pengajuan-batal">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-semibold">
            Pengajuan batal menunggu keputusan {ajuan && <span className="text-muted-foreground font-normal">({ajuan.length})</span>}{" "}
            <Hint>Diputus Kepala Cabang dari cabang FA itu. Pengaju tidak bisa memutus pengajuannya sendiri.</Hint>
          </div>
        </div>
        <Kosong err={errA} data={ajuan}>
          <div className="divide-y divide-border">
            {(ajuan || []).map((p) => (
              <div key={p.id} className="px-4 py-2 text-sm flex flex-wrap items-center gap-2 justify-between">
                <div><b>{p.fa_number}</b> · {p.hari_kerja_menunggu} hari kerja tanpa DP · {p.alasan}</div>
                {role === "KEPALA_CABANG" && (
                  <div className="space-x-2">
                    <Button size="sm" data-testid={`tindak-lanjut-setujui-batal-${p.id}`}
                            onClick={() => setDialog({
                              judul: `Setujui batal ${p.fa_number}? FA dibatalkan, tanda jadi hangus.`, wajib: false,
                              kirim: (alasan) => aksi(() => api.post(`/tindak-lanjut/pengajuan-batal/${p.id}/putus`, { setuju: true, alasan: alasan || null }),
                                "FA dibatalkan", muatSemua),
                            })}>Setujui</Button>
                    <Button size="sm" variant="outline" data-testid={`tindak-lanjut-tolak-batal-${p.id}`}
                            onClick={() => setDialog({
                              judul: `Tolak pengajuan batal ${p.fa_number}`, wajib: true,
                              kirim: (alasan) => aksi(() => api.post(`/tindak-lanjut/pengajuan-batal/${p.id}/putus`, { setuju: false, alasan }),
                                "Pengajuan ditolak", muatSemua),
                            })}>Tolak</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Kosong>
      </Card>
    </div>
  );
}

// --- BUSINESS-51 --------------------------------------------------------------------------
function BagianTunggakan({ role, setDialog }) {
  const [rows, err, muat] = useMuat("/tindak-lanjut/tunggakan");
  const [izin, errI, muatI] = useMuat("/tindak-lanjut/izin-tunggakan?status=DIAJUKAN");
  const muatSemua = async () => { await muat(); await muatI(); };
  const bolehAjukan = role === "CS" || role === "KEPALA_CABANG";

  return (
    <div className="space-y-4">
      <Card className="p-0 overflow-hidden" data-testid="tindak-lanjut-tunggakan">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-semibold">
            Customer menunggak lewat 7 hari kerja {rows && <span className="text-muted-foreground font-normal">({rows.length})</span>}{" "}
            <Hint>Tidak ada denda; barang tetap ditahan sampai lunas.</Hint>
          </div>
          <div className="text-xs text-muted-foreground">
            Tanda jadi, desain, dan FA baru tetap boleh jalan, tapi FA customer ini belum boleh masuk produksi sampai
            tunggakan lamanya lunas, kecuali disetujui Owner (satu izin untuk satu FA).
          </div>
        </div>
        <Kosong err={err} data={rows}>
          <div className="divide-y divide-border">
            {(rows || []).map((c) => (
              <div key={c.customer_id} className="px-4 py-2 text-sm flex flex-wrap items-center gap-2 justify-between"
                   data-testid={`tindak-lanjut-penunggak-${c.customer_id}`}>
                <div>
                  <b>{c.customer_name || "—"}</b> · {rupiah(c.total_idr)} ·{" "}
                  {c.tunggakan.map((t) => `${t.fa_number || "—"} (${t.umur_hari_kerja} hari kerja)`).join(", ")}
                  {c.izin_menunggu && <span className="text-warning-soft-foreground"> · izin menunggu Owner</span>}
                  {c.izin_siap_pakai && <span className="text-success-soft-foreground"> · izin Owner siap dipakai untuk satu FA</span>}
                </div>
                {bolehAjukan && !c.izin_menunggu && !c.izin_siap_pakai && (
                  <Button size="sm" variant="outline" data-testid={`tindak-lanjut-ajukan-izin-${c.customer_id}`}
                          onClick={() => setDialog({
                            judul: `Ajukan izin Owner agar satu FA ${c.customer_name || ""} boleh masuk produksi`, wajib: true,
                            kirim: (alasan) => aksi(() => api.post("/tindak-lanjut/izin-tunggakan", { customer_id: c.customer_id, alasan }),
                              "Pengajuan izin terkirim ke Owner", muatSemua),
                          })}>Ajukan izin Owner</Button>
                )}
              </div>
            ))}
          </div>
        </Kosong>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="tindak-lanjut-izin">
        <div className="px-4 py-3 border-b border-border">
          <div className="font-semibold">Izin menunggu Owner {izin && <span className="text-muted-foreground font-normal">({izin.length})</span>}</div>
        </div>
        <Kosong err={errI} data={izin}>
          <div className="divide-y divide-border">
            {(izin || []).map((i) => (
              <div key={i.id} className="px-4 py-2 text-sm flex flex-wrap items-center gap-2 justify-between">
                <div><b>{i.customer_name || "—"}</b> · {(i.tunggakan || []).length} piutang · {i.alasan}</div>
                {role === "OWNER" && (
                  <div className="space-x-2">
                    <Button size="sm" data-testid={`tindak-lanjut-setujui-izin-${i.id}`}
                            onClick={() => setDialog({
                              judul: "Setujui satu FA customer ini masuk produksi walau masih menunggak", wajib: false,
                              kirim: (alasan) => aksi(() => api.post(`/tindak-lanjut/izin-tunggakan/${i.id}/putus`, { setuju: true, alasan: alasan || null }),
                                "Izin disetujui", muatSemua),
                            })}>Setujui</Button>
                    <Button size="sm" variant="outline" data-testid={`tindak-lanjut-tolak-izin-${i.id}`}
                            onClick={() => setDialog({
                              judul: "Tolak izin FA masuk produksi", wajib: true,
                              kirim: (alasan) => aksi(() => api.post(`/tindak-lanjut/izin-tunggakan/${i.id}/putus`, { setuju: false, alasan }),
                                "Izin ditolak", muatSemua),
                            })}>Tolak</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Kosong>
      </Card>
    </div>
  );
}

// --- BUSINESS-52 --------------------------------------------------------------------------
function BagianBarang({ role }) {
  const [hanyaOwner, setHanyaOwner] = useState(role === "OWNER");
  const [rows, err] = useMuat("/tindak-lanjut/barang-belum-diambil", { keputusan_owner: hanyaOwner });
  const nama = useNama(rows);
  return (
    <Card className="p-0 overflow-hidden" data-testid="tindak-lanjut-barang">
      <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold">{hanyaOwner ? "Daftar keputusan Owner — lewat 90 hari" : "Barang jadi belum diambil"} {rows && <span className="text-muted-foreground font-normal">({rows.length})</span>}</div>
          <div className="text-xs text-muted-foreground">
            Disimpan paling lama 90 hari sejak barang siap, lunas atau belum.{" "}
            <Hint>H+30 dan H+60: Kepala Cabang mengirim pemberitahuan tertulis lewat WA CS. Lewat 90 hari: Owner memutus per kasus.</Hint>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setHanyaOwner((x) => !x)} data-testid="tindak-lanjut-saring-90">
          {hanyaOwner ? "Tampilkan semua" : "Hanya lewat 90 hari"}
        </Button>
      </div>
      <Kosong err={err} data={rows}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr><th className="text-left px-4 py-2">FA</th><th className="text-left">Customer</th>
                <th className="text-left">Hari sejak siap</th><th className="text-left">Keadaan</th></tr>
            </thead>
            <tbody>
              {(rows || []).map((f) => (
                <tr key={f.id} className="border-b border-border" data-testid={`tindak-lanjut-barang-${f.id}`}>
                  <td className="px-4 py-2 font-medium"><Link to={f.lunas ? `/serah-terima?fa=${f.id}` : `/fa-orders/${f.id}`} className="underline">{f.fa_number}</Link></td>
                  <td>{nama[f.customer_id] || "—"}</td>
                  <td>{f.umur_hari ?? "—"}</td>
                  <td>
                    {!f.lunas && <span className="text-warning-soft-foreground">belum lunas · </span>}
                    {f.keputusan_owner ? <span className="text-danger-soft-foreground">lewat 90 hari — keputusan Owner</span>
                      : f.tahap === "bj_h60" ? "pemberitahuan kedua (H+60)"
                        : f.tahap === "bj_h30" ? "pemberitahuan pertama (H+30)" : "menunggu diambil"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Kosong>
    </Card>
  );
}

function DialogAlasan({ judul, wajib, kirim, onTutup }) {
  const [alasan, setAlasan] = useState("");
  const [sibuk, setSibuk] = useState(false);
  const kurang = wajib && alasan.trim().length < 5;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <Card className="p-4 w-full max-w-md space-y-3" data-testid="tindak-lanjut-dialog">
        <div className="font-semibold">{judul}</div>
        <textarea value={alasan} onChange={(e) => setAlasan(e.target.value)} maxLength={1000} rows={3}
                  placeholder={wajib ? "Alasan (wajib, minimal 5 huruf)" : "Catatan (opsional)"}
                  className="w-full border border-input rounded px-2 py-1.5 text-sm" data-testid="tindak-lanjut-alasan" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onTutup}>Batal</Button>
          <Button disabled={sibuk || kurang} data-testid="tindak-lanjut-kirim"
                  onClick={async () => { setSibuk(true); const ok = await kirim(alasan.trim()); setSibuk(false); if (ok) onTutup(); }}>
            Kirim
          </Button>
        </div>
      </Card>
    </div>
  );
}
